/**
 * Talking to the DrBell API from the organisation console.
 *
 * Session cookies, not a shared secret. The console holds no credential of its
 * own: it is a view of what the signed-in person's org membership already
 * permits, and every route re-resolves that server-side.
 */

/** Same-origin by default: the org Worker proxies /api/* to the API. */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The refusal as sent, for the few a screen acts on (a time of day still in use). */
    readonly body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * What the API's refusal codes mean, in the words a care-home manager uses.
 *
 * The codes are a wire format, not a user interface. Showing `org_suspended`
 * to a nurse at 3am tells her nothing she can act on, and the raw code was
 * reaching the screen verbatim: not only in banners, where somebody might
 * shrug at it, but inside the confirm dialog where it is the ONLY explanation
 * of why the thing she pressed did not happen.
 *
 * Anything unmapped falls through unchanged rather than becoming "Something
 * went wrong", so an unknown refusal is still reportable to support.
 */
const REFUSALS: Record<string, string> = {
  already_owned_by_this_org: 'That person is already a resident here.',
  cannot_modify_owner: 'The owner\u2019s own role cannot be changed here. Transfer ownership first.',
  cannot_remove_owner: 'The owner cannot be removed. Transfer ownership first.',
  family_invites_disabled: 'This organisation does not allow inviting family members.',
  forbidden: 'Your role does not allow that.',
  not_an_org_admin: 'Only an owner or admin can do that.',
  not_found: 'That no longer exists \u2014 somebody may have removed it already.',
  not_owned_by_this_org: 'That resident belongs to a different organisation.',
  org_not_active: 'This organisation is still awaiting approval, so it cannot hold resident records yet.',
  phone_mismatch: 'That phone number does not match the invitation.',
  requester_cannot_approve: 'The person who asked for this cannot also approve it.',
  seat_limit_reached: 'Every contracted seat is in use.',
  slug_taken: 'That short name is already taken. Choose another.',
  sole_owner_of_organisation: 'They are the only owner. Make somebody else an owner first.',
  user_not_found: 'No DrBell account uses that number yet.',
  unsupported_type: 'That file is not a photo DrBell can read. Use a JPEG, PNG, WebP or HEIC.',
  not_an_image: 'That file is not really an image, whatever it is named.',
  too_large: 'That photo is too big. The limit is 12MB.',
  empty_body: 'Nothing arrived. Try choosing the photo again.',
  key_belongs_to_another_patient: 'That photo belongs to a different resident.',
};

/** Exported so a screen can add its own wording for a code it cares about. */
export function explainRefusal(code: string): string {
  return REFUSALS[code] ?? code;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });

  if (!response.ok) {
    let detail = response.statusText;
    let raw: Record<string, unknown> | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        used?: number;
        limit?: number;
      };
      raw = body as Record<string, unknown>;
      /*
       * The server's own sentence wins.
       *
       * `error` is a code and `message` is prose the handler wrote for this
       * exact situation. Mapping the code and ignoring the message produced
       * the worst kind of wrong: refusing an account deletion with "They are
       * the only owner. Make somebody else an owner first." — wording written
       * for removing a colleague, shown to the person it is about, while the
       * server had already said "You are the only owner of an organisation."
       *
       * The code mapping stays as the fallback, for the handlers that return
       * a bare code.
       */
      // Only ever text: an object here printed "[object Object]" (defect 142).
      detail = typeof body.message === 'string'
        ? body.message
        : typeof body.error === 'string' ? explainRefusal(body.error) : detail;
      // Seat and entitlement refusals carry the numbers that explain them.
      if (body.used !== undefined && body.limit !== undefined) {
        detail = `${detail} ${body.used} of ${body.limit} are taken.`;
      }
    } catch {
      /* body was not JSON */
    }
    throw new ApiError(response.status, detail, raw);
  }
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  /**
   * Raw bytes, not JSON.
   *
   * The media endpoint takes the image itself as the request body with the
   * photo's own content-type, so this cannot go through `call` — which sets
   * `content-type: application/json` and would have the server reject every
   * upload as "unsupported_type".
   */
  upload: async <T>(path: string, file: File): Promise<T> => {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': file.type },
      body: file,
    });
    if (!response.ok) {
      let detail = response.statusText;
      try {
        const body = (await response.json()) as { error?: string };
        detail = body.error ? explainRefusal(body.error) : detail;
      } catch {
        /* body was not JSON */
      }
      throw new ApiError(response.status, detail);
    }
    return (await response.json()) as T;
  },
  post: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  /** Some handlers are PATCH. Calling them with PUT simply does not route. */
  patch: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
};

/** One of a person's own named times of day (0051) — the name as typed. */
export interface RoutineTime {
  id: string;
  name: string;
  time: string;
}

// ── Shapes the console reads ────────────────────────────────────────────────

export type OrgRole = 'owner' | 'admin' | 'staff' | 'clinician' | 'billing';

/** Roles that may change staff or add residents. Mirrors the API's `MANAGERS`. */
export const MANAGERS: OrgRole[] = ['owner', 'admin'];

export interface Me {
  /** `locale` is the person's language: the footer offers "Report a wrong word" when it is not English (docs/31). */
  user: { id: string; name: string | null; phoneNumber: string | null; locale?: string } | null;
}

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: 'pending' | 'active' | 'suspended';
  seatLimit: number | null;
  /** The home's own time zone. */
  timezone: string;
  role: OrgRole;
  /** True when this membership is confined to a ward rather than the building. */
  scoped: boolean;
}

/**
 * An organisation this person has been invited to but has not joined.
 *
 * Deliberately a different type from OrgSummary. An invitation is not a
 * membership, and giving them the same shape is how one gets used where the
 * other was meant.
 */
export interface Invitation {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: 'pending' | 'active' | 'suspended';
  seatLimit: number | null;
  role: OrgRole;
  scoped: boolean;
  invitedAt: number | null;
}

export interface Member {
  userId: string;
  name: string | null;
  phone: string | null;
  role: OrgRole;
  status: string;
  /** Null means every patient the org owns; a list means one ward. */
  patientScope: string[] | null;
  acceptedAt: number | null;
}

export interface OrgDetail {
  org: { id: string; name: string; type: string; status: string; seatLimit: number | null } | null;
  seatsUsed: number;
  yourRole: OrgRole;
  members: Member[];
}

export interface Resident {
  id: string;
  displayName: string;
  locationNote: string | null;
  timezone: string;
  locale: string;
  presentationMode: 'elder' | 'standard' | 'kid';
}

export interface Seats {
  used: number;
  limit: number | null;
  subscription: {
    tier: string;
    status: string;
    seats: number | null;
    currentPeriodEnd: number | null;
  } | null;
}


// ── Rota (RX-ORG-14) ────────────────────────────────────────────────────────

export interface Shift {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  crossesMidnight: boolean;
  sortOrder: number;
}

export interface RotaAssignment {
  id: string;
  shiftId: string;
  userId: string;
  localDate: string;
  note: string | null;
  shiftName: string;
  startTime: string;
  endTime: string;
  /** First name, or null when the account has never been given one. */
  staffName: string | null;
  /** Masked, so two unnamed accounts can still be told apart. */
  staffPhoneHint: string | null;
  residents: Array<{ id: string; displayName: string; locationNote: string | null }>;
}

export interface OutstandingDose {
  id: string;
  patientId: string;
  dueAt: number;
  windowEnd: number;
  medicationName: string;
  isCritical: boolean;
  displayName: string;
  locationNote: string | null;
  overdue: boolean;
}

export interface RotaNow {
  now: number;
  /** The home's zone; every time on this screen is shown in it. */
  timeZone: string;
  /** Nobody on shift at this minute: alerts go to every eligible member. */
  nobodyOn: boolean;
  staff: Array<{
    id: string;
    userId: string;
    staffName: string | null;
    staffPhoneHint: string | null;
    shiftName: string;
    startTime: string;
    endTime: string;
    residents: number;
    /** When this person's shift ends. */
    endsAt: number;
    outstanding: OutstandingDose[];
  }>;
}


// ── Medicines (RX-ORG-15) ───────────────────────────────────────────────────

export interface Medication {
  id: string;
  name: string;
  nameAlt: string | null;
  brandName: string | null;
  strength: string | null;
  strengthUnit: string | null;
  form: string;
  instructionsText: string | null;
  prescribedBy: string | null;
  isCritical: boolean;
  stockCount: number | null;
  pillImageKey: string | null;
  stripImageKey: string | null;
  archivedAt: number | null;
}

export interface Schedule {
  id: string;
  type: 'fixed_times' | 'meal_relative' | 'interval' | 'prn';
  /** fixed_times only. */
  times: string[] | null;
  /** meal_relative only — the Indian-prescription case. */
  anchors: Array<{ meal: string; offsetMinutes: number; doseQuantity?: number }> | null;
  intervalHours: number | null;
  anchorTime: string | null;
  foodRelation: 'before_food' | 'with_food' | 'after_food' | 'empty_stomach' | 'any';
  doseQuantity: number;
  doseUnit: string;
  daysOfWeek: number[] | null;
  startDate: string;
  endDate: string | null;
  sourceNotation: string | null;
  archivedAt: number | null;
}

export interface MedicationEntry {
  medication: Medication;
  schedules: Schedule[];
}

/** What `POST /notation/parse` returns for shorthand like "1-0-1 x 7d". */
export interface ParsedNotation {
  ok: true;
  input: string;
  /** The substring actually recognised, so the UI can show what it read. */
  matched: string;
  draft: {
    type: 'meal_relative' | 'interval' | 'prn';
    anchors?: Array<{ meal: 'breakfast' | 'lunch' | 'dinner' | 'bedtime'; offsetMinutes: number; doseQuantity?: number }>;
    intervalHours?: number;
    dailyQuantity?: number;
  };
  foodRelation: Schedule['foodRelation'];
  durationDays?: number;
  /** `medium` means we guessed something the prescriber did not state. */
  confidence: 'high' | 'medium';
  warnings: string[];
  summary: string;
}

export interface ConsentState {
  documents: Array<{
    id: string;
    version: number;
    title: string;
    requiredBeforeFirstUse: boolean;
    accepted: boolean;
    body?: string;
  }>;
  pending: string[];
  blocksAddingMedication: boolean;
}


// ── Shift handover (RX-ORG-09) ──────────────────────────────────────────────

export interface HandoverDose {
  doseEventId: string;
  patientId: string;
  patientName: string;
  locationNote: string | null;
  medicationId: string;
  medicationName: string;
  strength: string | null;
  strengthUnit: string | null;
  doseQuantity: number;
  doseUnit: string;
  foodRelation: string | null;
  isCritical: boolean;
  dueAt: number;
  windowEnd: number;
  status: string;
}

export interface HandoverSlot {
  at: number;
  label: string;
  doses: HandoverDose[];
}

export interface Handover {
  from: number;
  to: number;
  timeZone: string;
  /** False when the query hit its row cap — the list is not the whole picture. */
  complete: boolean;
  outstanding: HandoverDose[];
  slots: HandoverSlot[];
  counts: {
    residents: number;
    due: number;
    outstanding: number;
    critical: number;
    given: number;
  };
}


// ── Bulk import (RX-ORG-05) ─────────────────────────────────────────────────

export interface ImportRow {
  /** 1-based line in the uploaded file, so an error can be found in Excel. */
  line: number;
  patient: { displayName: string; locationNote?: string | null; locale?: string } | null;
  errors: string[];
  warnings: string[];
}

export interface ImportPlan {
  rows: ImportRow[];
  /** Header names in the file that nothing was mapped from. */
  unmappedColumns: string[];
  counts: { total: number; ready: number; failed: number; warned: number };
}

export interface ImportResult {
  dryRun: boolean;
  imported?: number;
  plan: ImportPlan;
  seats?: { used: number; limit: number | null };
}


// ── Appointments (RX-RX-05) ────────────────────────────────────────────────

export type AppointmentKind = 'doctor_visit' | 'lab_test' | 'procedure' | 'other';
export type AppointmentStatus = 'scheduled' | 'attended' | 'missed' | 'cancelled';

export interface Appointment {
  id: string;
  patientId: string;
  kind: AppointmentKind;
  /** Exactly what somebody typed. Never generated, never rephrased. */
  title: string;
  doctorName: string | null;
  locationNote: string | null;
  notes: string | null;
  /** Wall clock where the patient is standing, like a schedule. */
  localDate: string;
  localTime: string | null;
  dueAt: number;
  /** Days before to remind — a list, e.g. [1] or [7, 1]. */
  remindDaysBefore: number[] | null;
  status: AppointmentStatus;
  archivedAt: number | null;
}


// ── Ownership transfers (RX-ORG-06) ─────────────────────────────────────────

export interface Transfer {
  id: string;
  patientId: string;
  patientName: string | null;
  fromType: 'user' | 'org';
  fromId: string;
  fromName: string;
  toType: 'user' | 'org';
  toId: string;
  toName: string;
  reason: string | null;
  requestedByUserId: string | null;
  requestedByName: string | null;
  /** The requester may not approve their own — the server says so. */
  youRequestedIt: boolean;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: number;
}

export interface Grant {
  id: string;
  granteeType: 'user' | 'org';
  granteeId: string;
  granteeName: string | null;
  role: string;
  status: string;
  invitePhone: string | null;
  acceptedAt: number | null;
}
